import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, Eye, Edit, Download, Send, Users, User, CreditCard, Clock, CheckCircle, AlertCircle, Shield, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEnhancedReports = ({ user, onLogout }) => {
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [families, setFamilies] = useState([]);
  const [owners, setOwners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportEditDialogOpen, setReportEditDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    totalReports: 0,
    pendingReports: 0,
    approvedReports: 0,
    sentReports: 0
  });
  
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    template_type: 'account',
    output_format: 'pdf',
    sections: [
      { name: 'Executive Summary', included: true, required: true },
      { name: 'Portfolio Overview', included: true, required: true },
      { name: 'Asset Allocation', included: true, required: false },
      { name: 'Performance Analysis', included: true, required: false },
      { name: 'Risk Analysis', included: true, required: false },
      { name: 'Recommendations', included: true, required: false },
      { name: 'Market Outlook', included: false, required: false },
      { name: 'Disclaimers', included: true, required: true }
    ]
  });
  
  const [reportForm, setReportForm] = useState({
    report_name: '',
    entity_type: 'account',
    entity_id: '',
    template_id: '',
    include_held_away: true
  });

  const [editForm, setEditForm] = useState({
    internal_notes: '',
    client_notes: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [templatesRes, reportsRes, familiesRes] = await Promise.all([
        axios.get(`${API}/report-templates`, config),
        axios.get(`${API}/review-reports`, config),
        axios.get(`${API}/families`, config)
      ]);
      
      setTemplates(templatesRes.data);
      const reportsData = reportsRes.data;
      setReports(reportsData);
      setFamilies(familiesRes.data.families || familiesRes.data);

      // Calculate admin statistics
      const pendingCount = reportsData.filter(r => r.status === 'draft' || r.status === 'reviewed').length;
      const approvedCount = reportsData.filter(r => r.status === 'approved').length;
      const sentCount = reportsData.filter(r => r.status === 'sent').length;

      setStats({
        totalReports: reportsData.length,
        pendingReports: pendingCount,
        approvedReports: approvedCount,
        sentReports: sentCount
      });

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async (familyId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/owners/family/${familyId}`, config);
      setOwners(response.data.owners || response.data);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to fetch owners');
    }
  };

  const fetchAccounts = async (ownerId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/accounts/owner/${ownerId}`, config);
      setAccounts(response.data.accounts || response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch accounts');
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/report-templates`, templateForm, config);
      toast.success('Report template created successfully');
      setTemplateDialogOpen(false);
      setTemplateForm({
        template_name: '',
        template_type: 'account',
        output_format: 'pdf',
        sections: templateForm.sections
      });
      fetchInitialData();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/review-reports/generate`, reportForm, config);
      toast.success('Report generated successfully');
      setReportDialogOpen(false);
      setReportForm({
        report_name: '',
        entity_type: 'account',
        entity_id: '',
        template_id: '',
        include_held_away: true
      });
      fetchInitialData();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleUpdateReport = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API}/review-reports/${selectedReport.id}`, editForm, config);
      toast.success('Report updated successfully');
      setReportEditDialogOpen(false);
      fetchInitialData();
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const toggleSection = (index, field) => {
    const newSections = [...templateForm.sections];
    newSections[index][field] = !newSections[index][field];
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <Edit className="w-4 h-4 text-gray-500" />;
      case 'reviewed': return <Eye className="w-4 h-4 text-ugmonk-grey" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'sent': return <Send className="w-4 h-4 text-ugmonk-dark" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'reviewed': return 'bg-[#fffdf0] text-ugmonk-dark';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'sent': return 'bg-[#fffdf0] text-ugmonk-dark';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd87a]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              System Enhanced Reports
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <Shield className="w-4 h-4 text-ugmonk-dark" />
              Admin oversight of all report generation and templates
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* Create Template Button */}
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                  <Plus className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-gray-900">Create System Report Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="template_name" className="text-gray-700">Template Name *</Label>
                      <Input
                        id="template_name"
                        value={templateForm.template_name}
                        onChange={(e) => setTemplateForm({ ...templateForm, template_name: e.target.value })}
                        required
                        className="bg-white border-gray-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="template_type" className="text-gray-700">Report Level *</Label>
                      <select
                        id="template_type"
                        value={templateForm.template_type}
                        onChange={(e) => setTemplateForm({ ...templateForm, template_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                      >
                        <option value="account">Account Level</option>
                        <option value="owner">Owner Level</option>
                        <option value="family">Family Level</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="output_format" className="text-gray-700">Output Format *</Label>
                    <select
                      id="output_format"
                      value={templateForm.output_format}
                      onChange={(e) => setTemplateForm({ ...templateForm, output_format: e.target.value })}
                      className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    >
                      <option value="pdf">PDF</option>
                      <option value="ppt">PowerPoint</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-gray-700 mb-3 block">Report Sections</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded p-3">
                      {templateForm.sections.map((section, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{section.name}</span>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Switch
                                checked={section.included}
                                onCheckedChange={() => toggleSection(index, 'included')}
                                disabled={section.required}
                              />
                              <span className="text-xs text-gray-500">Include</span>
                            </div>
                            {section.required && (
                              <span className="text-xs text-red-500">Required</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                    Create System Template
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Generate Report Button */}
            <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Report
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-gray-900">Generate System Report</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleGenerateReport} className="space-y-4">
                  <div>
                    <Label htmlFor="report_name" className="text-gray-700">Report Name *</Label>
                    <Input
                      id="report_name"
                      value={reportForm.report_name}
                      onChange={(e) => setReportForm({ ...reportForm, report_name: e.target.value })}
                      required
                      className="bg-white border-gray-300"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="template_select" className="text-gray-700">Template *</Label>
                    <select
                      id="template_select"
                      value={reportForm.template_id}
                      onChange={(e) => {
                        const template = templates.find(t => t.id === e.target.value);
                        setReportForm({ 
                          ...reportForm, 
                          template_id: e.target.value,
                          entity_type: template?.template_type || 'account'
                        });
                      }}
                      required
                      className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    >
                      <option value="">Select Template</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.template_name} ({template.template_type})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="entity_select" className="text-gray-700">
                      Select {reportForm.entity_type || 'Entity'} *
                    </Label>
                    {reportForm.entity_type === 'family' && (
                      <select
                        value={reportForm.entity_id}
                        onChange={(e) => setReportForm({ ...reportForm, entity_id: e.target.value })}
                        required
                        className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                      >
                        <option value="">Select Family</option>
                        {families.map(family => (
                          <option key={family.id} value={family.id}>
                            {family.family_name} ({family.rm_name})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={reportForm.include_held_away}
                      onCheckedChange={(checked) => setReportForm({ ...reportForm, include_held_away: checked })}
                    />
                    <Label className="text-gray-700">Include held-away accounts</Label>
                  </div>
                  
                  <Button type="submit" className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                    Generate System Report
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Admin Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0] rounded-xl p-6 border border-ugmonk-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <FileText className="w-6 h-6 text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Reports</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalReports}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-200">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Reviews</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingReports}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-200">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{stats.approvedReports}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-xl p-6 border border-ugmonk-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <Send className="w-6 h-6 text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Sent to Clients</p>
                <p className="text-2xl font-bold text-gray-900">{stats.sentReports}</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="reports" className="space-y-6">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="reports" className="data-[state=active]:bg-white">All Reports</TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-white">System Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="reports" className="space-y-4">
            <div className="grid gap-4">
              {reports.map((report) => (
                <Card key={report.id} className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{report.report_name}</h3>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(report.status)}`}>
                            {getStatusIcon(report.status)}
                            {report.status}
                          </div>
                          <div className="text-sm text-gray-500">
                            Version {report.version}
                          </div>
                          <span className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded-full">
                            Admin View
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <span className="flex items-center gap-1">
                            {report.entity_type === 'family' && <Users className="w-4 h-4" />}
                            {report.entity_type === 'owner' && <User className="w-4 h-4" />}
                            {report.entity_type === 'account' && <CreditCard className="w-4 h-4" />}
                            {report.entity_type} Level
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </span>
                          <span>By: {report.generated_by}</span>
                        </div>
                        
                        {report.internal_notes && (
                          <div className="mb-2">
                            <span className="text-xs text-gray-500">Internal Notes:</span>
                            <p className="text-sm text-gray-700">{report.internal_notes}</p>
                          </div>
                        )}
                        
                        {report.client_notes && (
                          <div className="mb-2">
                            <span className="text-xs text-gray-500">Client Notes:</span>
                            <p className="text-sm text-gray-700">{report.client_notes}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedReport(report);
                            setEditForm({
                              internal_notes: report.internal_notes || '',
                              client_notes: report.client_notes || '',
                              status: report.status
                            });
                            setReportEditDialogOpen(true);
                          }}
                          className="border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" className="bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {reports.length === 0 && (
                <Card className="border-gray-200">
                  <CardContent className="p-12 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports Generated</h3>
                    <p className="text-gray-600 mb-4">System reports will appear here once generated by RMs</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="templates" className="space-y-4">
            <div className="grid gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="border-gray-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-gray-900">{template.template_name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-[#fffdf0] text-ugmonk-dark text-xs rounded-full">
                          {template.template_type} Level
                        </span>
                        <span className="px-2 py-1 bg-[#fffdf0] text-ugmonk-dark text-xs rounded-full">
                          {template.output_format.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          System Template
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600">
                      Sections: {template.sections?.filter(s => s.included).length || 0} included
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Created: {new Date(template.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      By: {template.created_by}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {templates.length === 0 && (
                <Card className="border-gray-200">
                  <CardContent className="p-12 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No System Templates</h3>
                    <p className="text-gray-600 mb-4">Create your first system template to get started</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Report Edit Dialog */}
        <Dialog open={reportEditDialogOpen} onOpenChange={setReportEditDialogOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Admin: Edit Report</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateReport} className="space-y-4">
              <div>
                <Label htmlFor="internal_notes" className="text-gray-700">Internal Notes</Label>
                <Textarea
                  id="internal_notes"
                  value={editForm.internal_notes}
                  onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
                  className="bg-white border-gray-300"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="client_notes" className="text-gray-700">Client-Visible Notes</Label>
                <Textarea
                  id="client_notes"
                  value={editForm.client_notes}
                  onChange={(e) => setEditForm({ ...editForm, client_notes: e.target.value })}
                  className="bg-white border-gray-300"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="status" className="text-gray-700">Report Status</Label>
                <select
                  id="status"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                >
                  <option value="draft">Draft</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                  <option value="sent">Sent</option>
                </select>
              </div>
              
              <Button type="submit" className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                Update Report (Admin)
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminEnhancedReports;
